import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

/* ── Scoped styles ─────────────────────────────────────────────────────────── */
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&family=DM+Sans:wght@400;500;600&family=DM+Mono&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .vp-root {
    min-height: 100vh;
    background: #F7F5F0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 32px 16px;
    position: relative;
    overflow: hidden;
    font-family: 'DM Sans', sans-serif;
  }

  /* Subtle background blobs */
  .vp-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);
    pointer-events: none;
    z-index: 0;
    opacity: 0.55;
  }
  .vp-blob-1 {
    width: 480px; height: 480px;
    background: radial-gradient(circle, #D4F5E2 0%, transparent 70%);
    top: -140px; right: -100px;
    animation: vp-drift1 12s ease-in-out infinite;
  }
  .vp-blob-2 {
    width: 360px; height: 360px;
    background: radial-gradient(circle, #FFF3CD 0%, transparent 70%);
    bottom: -100px; left: -80px;
    animation: vp-drift2 15s ease-in-out infinite;
  }
  .vp-blob-3 {
    width: 220px; height: 220px;
    background: radial-gradient(circle, #E8F4FD 0%, transparent 70%);
    top: 40%; left: 10%;
    animation: vp-drift1 18s ease-in-out infinite reverse;
  }

  @keyframes vp-drift1 {
    0%, 100% { transform: translate(0, 0) scale(1); }
    50%       { transform: translate(20px, 30px) scale(1.06); }
  }
  @keyframes vp-drift2 {
    0%, 100% { transform: translate(0, 0) scale(1); }
    50%       { transform: translate(-18px, -22px) scale(1.04); }
  }

  /* Card */
  .vp-card {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 520px;
    background: rgba(255, 255, 255, 0.82);
    backdrop-filter: blur(24px);
    -webkit-backdrop-filter: blur(24px);
    border: 1px solid rgba(255,255,255,0.9);
    border-radius: 32px;
    padding: 56px 52px 48px;
    box-shadow:
      0 0 0 1px rgba(0,0,0,0.04),
      0 8px 24px rgba(0,0,0,0.06),
      0 32px 80px rgba(0,0,0,0.09);
    animation: vp-card-in 0.55s cubic-bezier(0.22, 1, 0.36, 1) both;
    text-align: center;
  }

  @media (max-width: 600px) {
    .vp-card { padding: 44px 28px 36px; }
  }

  @keyframes vp-card-in {
    from { opacity: 0; transform: translateY(24px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  /* ── Envelope illustration ── */
  .vp-envelope-wrap {
    width: 96px;
    height: 96px;
    margin: 0 auto 28px;
    position: relative;
  }

  .vp-env-ring {
    position: absolute;
    inset: -8px;
    border-radius: 50%;
    border: 2px dashed rgba(16, 185, 129, 0.25);
    animation: vp-spin 18s linear infinite;
  }

  @keyframes vp-spin {
    to { transform: rotate(360deg); }
  }

  .vp-env-bg {
    width: 96px;
    height: 96px;
    border-radius: 50%;
    background: linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%);
    box-shadow:
      0 0 0 10px rgba(16, 185, 129, 0.08),
      0 8px 24px rgba(16, 185, 129, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    animation: vp-float 3.5s ease-in-out infinite;
  }

  @keyframes vp-float {
    0%, 100% { transform: translateY(0); }
    50%       { transform: translateY(-6px); }
  }

  /* Floating dots around envelope */
  .vp-dot {
    position: absolute;
    border-radius: 50%;
    background: #10b981;
    animation: vp-dot-pulse 2.5s ease-in-out infinite;
  }
  .vp-dot-1 { width: 7px; height: 7px; top: 6px; right: 10px; animation-delay: 0s; opacity: 0.7; }
  .vp-dot-2 { width: 5px; height: 5px; bottom: 8px; right: 4px; animation-delay: 0.6s; opacity: 0.5; }
  .vp-dot-3 { width: 4px; height: 4px; bottom: 12px; left: 6px; animation-delay: 1.1s; opacity: 0.4; }

  @keyframes vp-dot-pulse {
    0%, 100% { transform: scale(1); opacity: 0.7; }
    50%       { transform: scale(1.5); opacity: 0.2; }
  }

  /* ── Text ── */
  .vp-tag {
    font-family: 'DM Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: #10b981;
    margin-bottom: 10px;
    display: block;
    animation: vp-fadein 0.5s 0.2s both;
  }

  .vp-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(2rem, 6vw, 2.75rem);
    font-weight: 700;
    color: #1A1714;
    letter-spacing: -0.5px;
    line-height: 1.15;
    margin-bottom: 14px;
    animation: vp-fadein 0.5s 0.3s both;
  }

  .vp-title em {
    font-style: italic;
    color: #10b981;
  }

  .vp-body {
    font-size: 15px;
    line-height: 1.65;
    color: #6B6560;
    max-width: 380px;
    margin: 0 auto 32px;
    animation: vp-fadein 0.5s 0.4s both;
  }

  @keyframes vp-fadein {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  /* ── Steps ── */
  .vp-steps {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 32px;
    text-align: left;
    animation: vp-fadein 0.5s 0.5s both;
  }

  .vp-step {
    display: flex;
    align-items: center;
    gap: 12px;
    background: #FAFAF8;
    border: 1px solid rgba(0,0,0,0.06);
    border-radius: 14px;
    padding: 12px 16px;
    transition: background 0.2s, border-color 0.2s;
  }

  .vp-step:hover {
    background: #F0FDF8;
    border-color: rgba(16, 185, 129, 0.2);
  }

  .vp-step-num {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: linear-gradient(135deg, #10b981, #059669);
    color: #fff;
    font-family: 'DM Mono', monospace;
    font-size: 11px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .vp-step-text {
    font-size: 13.5px;
    color: #3D3830;
    line-height: 1.4;
  }

  /* ── Button ── */
  .vp-btn-wrap {
    animation: vp-fadein 0.5s 0.6s both;
    width: 100%;
  }

  .vp-btn {
    width: 100%;
    height: 52px;
    border-radius: 16px;
    border: none;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    color: #fff;
    font-family: 'DM Sans', sans-serif;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: 0.01em;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(16, 185, 129, 0.4), 0 1px 4px rgba(0,0,0,0.08);
    transition: transform 0.2s cubic-bezier(0.22,1,0.36,1), box-shadow 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 9px;
  }

  .vp-btn::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(255,255,255,0.22) 0%, transparent 55%);
    pointer-events: none;
  }

  .vp-btn:not(:disabled):hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(16, 185, 129, 0.45), 0 2px 8px rgba(0,0,0,0.1);
  }

  .vp-btn:not(:disabled):active {
    transform: translateY(0);
    box-shadow: 0 2px 10px rgba(16, 185, 129, 0.35);
  }

  .vp-btn:disabled {
    opacity: 0.75;
    cursor: not-allowed;
  }

  /* Spinner */
  .vp-spinner {
    width: 18px;
    height: 18px;
    border: 2.5px solid rgba(255,255,255,0.35);
    border-top-color: #fff;
    border-radius: 50%;
    animation: vp-spin-fast 0.7s linear infinite;
    flex-shrink: 0;
  }

  @keyframes vp-spin-fast {
    to { transform: rotate(360deg); }
  }

  /* ── Footer ── */
  .vp-footer {
    margin-top: 24px;
    animation: vp-fadein 0.5s 0.7s both;
  }

  .vp-footer-text {
    font-size: 13px;
    color: #A09890;
    line-height: 1.55;
  }

  .vp-resend {
    background: none;
    border: none;
    color: #10b981;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    padding: 0;
    text-decoration: underline;
    text-decoration-color: transparent;
    transition: text-decoration-color 0.2s;
  }

  .vp-resend:hover {
    text-decoration-color: #10b981;
  }

  .vp-resend:disabled {
    color: #A09890;
    cursor: default;
  }

  /* Resend feedback */
  .vp-resend-toast {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: #ECFDF5;
    border: 1px solid rgba(16,185,129,0.2);
    border-radius: 8px;
    padding: 4px 10px;
    font-family: 'DM Mono', monospace;
    font-size: 11px;
    letter-spacing: 0.08em;
    color: #059669;
    margin-top: 10px;
    animation: vp-fadein 0.3s both;
  }

  /* Divider */
  .vp-divider {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 28px 0;
  }
  .vp-divider-line { flex: 1; height: 1px; background: rgba(0,0,0,0.07); }
  .vp-divider-text {
    font-family: 'DM Mono', monospace;
    font-size: 9px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: #C0B8B0;
  }
`;

/* ── Component ─────────────────────────────────────────────────────────────── */
const VerificationPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [resent, setResent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(false);
  const styleRef = useRef(null);

  useEffect(() => {
    if (!styleRef.current) {
      const el = document.createElement("style");
      el.textContent = css;
      document.head.appendChild(el);
      styleRef.current = el;
    }
    return () => {
      styleRef.current?.remove();
      styleRef.current = null;
    };
  }, []);

  const handleProceed = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      navigate("/login");
    }, 1800);
  };

  const handleResend = () => {
    if (resendCooldown) return;
    setResent(true);
    setResendCooldown(true);
    // simulate API call
    setTimeout(() => setResendCooldown(false), 30000);
  };

  return (
    <div className="vp-root">
      {/* Background blobs */}
      <div className="vp-blob vp-blob-1" />
      <div className="vp-blob vp-blob-2" />
      <div className="vp-blob vp-blob-3" />

      <div className="vp-card">
        {/* Envelope icon */}
        <div className="vp-envelope-wrap">
          <div className="vp-env-ring" />
          <div className="vp-env-bg">
            <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
              {/* Envelope body */}
              <rect
                x="5"
                y="11"
                width="34"
                height="24"
                rx="5"
                fill="#10b981"
                opacity="0.12"
                stroke="#10b981"
                strokeWidth="1.5"
              />
              {/* Envelope flap */}
              <path
                d="M5 16 L22 26 L39 16"
                stroke="#10b981"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
              {/* Checkmark badge */}
              <circle cx="33" cy="13" r="7" fill="#fff" />
              <circle cx="33" cy="13" r="6" fill="#10b981" />
              <path
                d="M30 13 L32.2 15.3 L36.5 10.5"
                stroke="#fff"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
          </div>
          <div className="vp-dot vp-dot-1" />
          <div className="vp-dot vp-dot-2" />
          <div className="vp-dot vp-dot-3" />
        </div>

        {/* Text */}
        <span className="vp-tag">✦ Action Required</span>
        <h1 className="vp-title">
          Check your <em>inbox</em>
        </h1>
        <p className="vp-body">
          We sent a verification link to your email address. Click it to
          activate your account and get started.
        </p>

        {/* Steps */}
        <div className="vp-steps">
          {[
            "Open your email inbox",
            "Find our message and click the link",
            "You're in — enjoy the platform",
          ].map((text, i) => (
            <div className="vp-step" key={i}>
              <div className="vp-step-num">
                {String(i + 1).padStart(2, "0")}
              </div>
              <span className="vp-step-text">{text}</span>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="vp-btn-wrap">
          <button className="vp-btn" onClick={handleProceed} disabled={loading}>
            {loading ? (
              <>
                <span className="vp-spinner" />
                Redirecting to login…
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M3 8h10M9 4l4 4-4 4"
                    stroke="#fff"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                I verified — take me to login
              </>
            )}
          </button>
        </div>

        {/* Divider */}
        <div className="vp-divider">
          <div className="vp-divider-line" />
          <span className="vp-divider-text">No email?</span>
          <div className="vp-divider-line" />
        </div>

        {/* Footer */}
        <div className="vp-footer">
          <p className="vp-footer-text">
            Check your spam folder, or{" "}
            <button
              className="vp-resend"
              onClick={handleResend}
              disabled={resendCooldown}
            >
              {resendCooldown ? "email sent ✓" : "resend the email"}
            </button>
            .
          </p>
          {resent && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: 10,
              }}
            >
              <div className="vp-resend-toast">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <circle
                    cx="6"
                    cy="6"
                    r="5.5"
                    stroke="#059669"
                    strokeWidth="1"
                  />
                  <path
                    d="M4 6l1.5 1.5L8 4"
                    stroke="#059669"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Sent — check your inbox
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerificationPage;
